import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, MonitorSmartphone } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchUserSessions, type AdminSession } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A3.10 — Foydalanuvchi sessiyalari va qurilmalari. GET /admin/users/:id/sessions
 * (backend `3fbb947`). Token-hash'lar QAYTARILMAYDI — faqat metama'lumot.
 */
export function UserSessions() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminSession>[]>(
    () => [
      { key: 'platform', header: t.users.platform, type: 'text', width: 110 },
      { key: 'device_model', header: t.users.device, type: 'text', minWidth: 160 },
      { key: 'os_version', header: t.users.osVersion, type: 'text', width: 120 },
      { key: 'app_version', header: t.users.appVersion, type: 'text', width: 110 },
      { key: 'last_ip', header: t.col.ip, type: 'id', width: 160, piiLevel: 'L3' },
      { key: 'last_active', header: t.users.lastSeen, type: 'date', width: 150 },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 150, hiddenByDefault: true },
      {
        key: 'active',
        header: t.col.status,
        type: 'enum',
        width: 130,
        accessor: (r) => (r.active ? 'active' : 'revoked'),
        options: [
          { value: 'active', label: t.status.active, status: 'active' },
          { value: 'revoked', label: t.users.revoked, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserSessions(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.users.sessionsTitle}
        screenId="A3.10"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminSession>
        screenId="A3.10"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <MonitorSmartphone size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
